import React, { useEffect, useState } from "react";
import { View, StyleSheet, Platform } from "react-native";

import {
  Text,
  Card,
  ActivityIndicator,
  useTheme,
  Portal,
  Snackbar,
} from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import AppHeader from "../components/AppHeader";
import AvailableWordsDisplay from "../components/AvailableWordsDisplay";
import EndOfGameBar from "../components/EndOfGameBar";
import PuzzleHeading from "../components/PuzzleHeading";
import AppButton from "../components/ui/AppButton";
import LoadingOverlay, { LOADING_MESSAGE } from "../components/LoadingOverlay";
import TutorialBanner from "../components/TutorialBanner";
import { Footer } from "../components/Footer";

import GraphVisualization from "../components/GraphVisualization";
import PathDisplayConfigurator from "../components/PathDisplayConfigurator";
import PlayerPathDisplay from "../components/PlayerPathDisplay";
// Removed UpgradePrompt - no longer needed in local-only mode
import WordDefinitionDialog from "../components/WordDefinitionDialog";

import { useGameStore } from "../stores/useGameStoreFacade";
import { useDailyChallengeStore } from "../stores/useDailyChallengeStore";
import { useTutorial } from "../context/TutorialContext";
import { useAssetStore } from "../stores/useAssetStore";
import { useAppReadyStore } from "../stores/useAppReadyStore";
import type { ExtendedTheme } from "../theme/SynapseTheme";
import type { MoveJudgement } from "../utils/gameReportUtils";

interface GameScreenProps {
  // Removed auth-related props - local-only mode
  onLegalPageRequest?: (
    page: "terms" | "privacy" | "dmca" | "about" | "contact",
  ) => void;
}

const GameScreen: React.FC<GameScreenProps> = ({ onLegalPageRequest }) => {
  // const navigation = useNavigation<NavigationProp>(); // Removing unused navigation variable
  const { customColors, colors } = useTheme() as ExtendedTheme;

  // UI state
  const [_optionsVisible, _setOptionsVisible] = useState(false);
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [selectedWordPathIndex, setSelectedWordPathIndex] = useState<
    number | null
  >(null);
  const [definitionVisible, setDefinitionVisible] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [gameWasRestored, setGameWasRestored] = useState(false);

  // Game state from useGameStore
  const gameStatus = useGameStore((state) => state.gameStatus);
  const startGame = useGameStore((state) => state.startGame);
  const selectWord = useGameStore((state) => state.selectWord);
  const giveUp = useGameStore((state) => state.giveUp);
  const currentWord = useGameStore((state) => state.currentWord);
  const playerPath = useGameStore((state) => state.playerPath);
  const optimalPath = useGameStore((state) => state.optimalPath);
  const suggestedPathFromCurrent = useGameStore(
    (state) => state.suggestedPathFromCurrent,
  );
  const optimalChoices = useGameStore((state) => state.optimalChoices);
  const loadInitialData = useGameStore((state) => state.loadInitialData);
  const errorLoadingData = useGameStore((state) => state.errorLoadingData);
  const hasPendingChallenge = useGameStore(
    (state) => state.hasPendingChallenge,
  );
  const pendingChallengeWords = useGameStore(
    (state) => state.pendingChallengeWords,
  );
  const startChallengeGameAction = useGameStore(
    (state) => state.startChallengeGame,
  );
  const isChallenge = useGameStore((state) => state.isChallenge);
  const isDailyChallenge = useGameStore((state) => state.isDailyChallenge);

  // Asset state from useAssetStore
  const { graphData, isLoading: isLoadingAssets } = useAssetStore((state) => ({
    graphData: state.graphData,
    isLoading: state.isLoading,
  }));

  // Combined loading state
  const isLoading =
    useGameStore(
      (state) => state.isLoadingData || state.gameStatus === "loading",
    ) || isLoadingAssets;

  const appReady = useAppReadyStore((state) => state.appReady);

  // Tutorial guidance for the on-board walkthrough
  const { guidance } = useTutorial();

  // Get daily challenge state from the new store
  const { remainingFreeGames: _remainingFreeGames } = useDailyChallengeStore(
    (state) => ({
      remainingFreeGames: state.remainingFreeGames,
    }),
  );

  // Upgrade prompts removed

  // Load initial data on mount and check if a game was restored.
  useEffect(() => {
    const performInitialLoad = async () => {
      const restored = await loadInitialData();
      setGameWasRestored(restored);
      setInitialLoadComplete(true);
    };

    // We only want this to run once.
    if (!initialLoadComplete) {
      performInitialLoad();
    }
  }, [loadInitialData, initialLoadComplete]);

  // Handle challenge game from deep link if pending - THIS MUST RUN FIRST
  useEffect(() => {
    // Actions and state are now available from store hooks directly
    if (
      initialLoadComplete &&
      graphData &&
      hasPendingChallenge &&
      pendingChallengeWords
    ) {
      console.log("🎮 GameScreen: Starting pending challenge");
      startChallengeGameAction(
        pendingChallengeWords.startWord,
        pendingChallengeWords.targetWord,
      )
        .then(() => {
          // If successful, the store would have set isChallenge, gameStatus etc.
          // Store also clears pending flags. No explicit action needed here if store manages state correctly.
          console.log("🎮 GameScreen: Challenge started successfully");
        })
        .catch((_actionError) => {
          // Catch rejection from startChallengeGameAction
          // The action itself should ideally set errorLoadingData in the store upon failure.
          // We check the store for that error message.
          const currentErrorFromStore =
            useGameStore.getState().errorLoadingData;
          const messageToShow =
            currentErrorFromStore ||
            "Failed to start challenge. Trying a random game.";

          setSnackbarMessage(messageToShow);
          setSnackbarVisible(true);

          // Fallback: attempt to start a new random game after showing the error.
          startGame(); // Call startGame to fall back to a random game
        });
    }
  }, [
    initialLoadComplete,
    graphData,
    hasPendingChallenge,
    pendingChallengeWords,
    startChallengeGameAction,
    startGame,
    // No need to depend on setHasPendingChallenge or setPendingChallengeWords here
    // as we're reacting to the state values, and the store is responsible for setting them.
  ]);

  // AppInitializer now handles all startup flow decisions
  // GameScreen no longer auto-starts games to avoid race conditions
  // The App.tsx initialization flow via AppInitializer is the single source of truth
  useEffect(() => {
    // Only log status for debugging - no auto-start logic
    if (initialLoadComplete) {
      console.log("🎮 GameScreen: Initial load complete", {
        graphData: !!graphData,
        gameWasRestored,
        gameStatus,
        hasPendingChallenge,
        isChallenge,
        isDailyChallenge,
        errorLoadingData,
      });
    }
  }, [
    initialLoadComplete,
    graphData,
    gameWasRestored,
    gameStatus,
    hasPendingChallenge,
    isChallenge,
    isDailyChallenge,
    errorLoadingData,
  ]);

  // Determine if we should show the path display options
  // Don't automatically show path options on game end - they're now in the modal
  const showPathOptions = false;

  // Handle word selection for display definition
  const handleShowDefinition = (word: string, pathIndex?: number) => {
    setSelectedWord(word);
    setSelectedWordPathIndex(pathIndex ?? null);
    setDefinitionVisible(true);
  };

  // Handle dismissing the definition dialog
  const handleDismissDefinition = () => {
    setDefinitionVisible(false);
    setSelectedWord(null);
    setSelectedWordPathIndex(null);
  };

  // Handle word selection for gameplay
  const handleSelectWord = (word: string) => {
    // Check if the word is a valid neighbor of the current word
    if (graphData && currentWord && graphData[currentWord]?.edges[word]) {
      // It's a valid neighbor, so select it regardless of whether it's in the path
      selectWord(word);
      return;
    }

    // If we get here, it's not a valid neighbor.
    // This shouldn't happen with the UI, but handle it just in case
    setSnackbarMessage(
      `Invalid move: ${word} is not connected to ${currentWord}.`,
    );
  };

  // Handle give up action
  const handleGiveUp = () => {
    giveUp();
  };

  // Handle snackbar dismiss
  const onDismissSnackbar = () => setSnackbarVisible(false);

  // Render the gold path (optimalPath) once the game is over
  const renderGoldPath = () => {
    if (!showPathOptions || !optimalPath || optimalPath.length < 2) return null;

    // This function displays the static gold path, not player choices.
    // Coloring here should remain based on the gold path itself.
    return (
      <View style={styles.pathContent}>
        {optimalPath.map((word, index) => (
          <React.Fragment key={`gold-${index}`}>
            <Text
              style={{
                ...styles.pathWord,
                color: customColors.globalOptimalNode,
              }}
              onPress={() => handleShowDefinition(word)}
            >
              {word}
            </Text>
            {index < optimalPath.length - 1 && (
              <Text style={styles.pathArrow}> → </Text>
            )}
          </React.Fragment>
        ))}
      </View>
    );
  };

  // Upgrade functions removed

  // Render loading indicator if data is loading or game status is loading
  if (isLoading) {
    return (
      <SafeAreaView style={styles.centeredContainer} testID="loading-indicator">
        <ActivityIndicator
          testID="activity-indicator"
          animating
          size="large"
          color={customColors.currentNode}
        />
        <Text style={[styles.loadingText, { color: colors.onSurface }]}>
          {LOADING_MESSAGE}
        </Text>
      </SafeAreaView>
    );
  }

  // Show error state if there's an error
  if (errorLoadingData) {
    return (
      <SafeAreaView style={styles.centeredContainer} testID="error-state">
        <Text style={styles.errorText}>{errorLoadingData}</Text>
        <AppButton label="Try again" onPress={() => startGame()} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} testID="game-screen">
      <AppHeader
        onNewGame={startGame}
        onGiveUp={handleGiveUp}
        newGameDisabled={gameStatus === "playing" || isLoading}
        giveUpDisabled={gameStatus !== "playing" || isLoading}
        appReady={appReady}
      />
      <View style={{ flex: 1 }}>
        <View
          style={styles.gameContainer}
          testID={gameStatus === "playing" ? "game-interface" : "idle-state"}
        >
          <PuzzleHeading />
          <View style={[styles.graphContainer, styles.transparentBackground]}>
            {isLoading ? (
              <View
                style={{
                  flex: 1,
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 20,
                }}
              >
                <ActivityIndicator
                  size="large"
                  animating={true}
                  color={colors.primary}
                />
                <Text
                  style={{
                    marginTop: 16,
                    color: colors.onSurface,
                    textAlign: "center",
                  }}
                >
                  {!graphData ? "Loading word network..." : "Starting game..."}
                </Text>
                {!graphData && (
                  <Text
                    style={{
                      marginTop: 8,
                      color: colors.onSurfaceVariant,
                      textAlign: "center",
                      fontSize: 12,
                    }}
                  >
                    1.5MB semantic graph loading
                  </Text>
                )}
              </View>
            ) : (
              <GraphVisualization
                onMove={handleSelectWord}
                hintWord={guidance?.highlightWord ?? null}
              />
            )}
          </View>
          {/* Player Path Container */}
          <View style={[styles.pathCard, styles.transparentBackground]}>
            <View style={styles.transparentContent}>
              <PlayerPathDisplay
                playerPath={playerPath}
                optimalChoices={optimalChoices as MoveJudgement[]}
                suggestedPath={suggestedPathFromCurrent}
                onWordDefinition={handleShowDefinition}
              />
            </View>
          </View>
          {/* Gold path container (only shown when game is over) */}
          {showPathOptions && (
            <View style={[styles.pathCard, styles.transparentBackground]}>
              {renderGoldPath()}
            </View>
          )}
          {/* Tutorial guidance callout above the word chips; on the won board
              it is her Done card (it renders nothing once the walkthrough ends) */}
          <TutorialBanner />
          {/* Available Words Display (only shown when playing) */}
          {gameStatus === "playing" && (
            <AvailableWordsDisplay
              onWordSelect={handleSelectWord}
              highlightWord={guidance?.highlightWord ?? null}
              onWordDefinition={handleShowDefinition}
            />
          )}
          {/* After a win or give-up: View report where the chips were */}
          <EndOfGameBar />
          {/* Only show path display options when game is over */}
          {showPathOptions && (
            <Card
              style={[styles.optionsCard, { backgroundColor: colors.surface }]}
            >
              <Card.Content>
                <Text
                  variant="labelMedium"
                  style={[styles.optionsLabel, { color: colors.onSurface }]}
                >
                  Path Display:
                </Text>
                <PathDisplayConfigurator compact={true} />
              </Card.Content>
            </Card>
          )}
        </View>
        {/* Dialogs and Portals */}
        <Portal>
          {/* Word Definition Dialog */}
          <WordDefinitionDialog
            visible={definitionVisible}
            word={selectedWord || ""}
            pathIndexInPlayerPath={selectedWordPathIndex}
            onDismiss={handleDismissDefinition}
          />
          {/* Snackbar for messages */}
          <Snackbar
            visible={snackbarVisible}
            onDismiss={onDismissSnackbar}
            style={{ backgroundColor: colors.surface }}
          >
            {snackbarMessage}
          </Snackbar>
        </Portal>

        {/* Global Modals - Available in all states */}
        {!appReady && <LoadingOverlay />}
      </View>

      {/* Footer only for web platform */}
      {Platform.OS === "web" && (
        <Footer onLegalPageRequest={onLegalPageRequest} />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  transparentBackground: {
    backgroundColor: "transparent",
  },
  pathCard: {
    margin: 5,
    maxWidth: 600,
    alignSelf: "center",
    width: "100%",
    alignItems: "center",
    borderRadius: 8,
  },
  pathHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  pathScrollContainer: {
    maxHeight: 100,
  },
  pathScrollContent: {
    flexGrow: 1,
    justifyContent: "center",
  },
  pathContent: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 4,
    gap: 8,
    width: "100%",
  },
  pathLabel: {
    fontWeight: "600",
    marginBottom: 4,
  },
  pathWord: {
    fontSize: 16,
    fontWeight: "500",
  },
  pathArrow: {
    fontSize: 16,
    opacity: 0.7,
    marginHorizontal: 8,
  },
  pathEllipsis: {
    fontSize: 16,
    opacity: 0.7,
    letterSpacing: 3,
  },
  pathDot: {
    fontSize: 10,
    opacity: 0.5,
    marginHorizontal: 2,
  },
  gameContainer: {
    flex: 1,
    margin: 5,
    maxWidth: 600,
    alignSelf: "center",
    width: "100%",
  },
  graphContainer: {
    flex: 1,
    padding: 0,
    borderRadius: 8,
    overflow: "hidden",
  },
  controls: {
    flexDirection: "row",
    justifyContent: "center",
    padding: 15,
    gap: 12,
  },
  button: {
    minWidth: 120,
    borderWidth: 1,
  },
  optionsCard: {
    margin: 10,
    marginTop: 0,
    borderRadius: 8,
  },
  optionsLabel: {
    marginBottom: 8,
  },
  transparentContent: {
    backgroundColor: "transparent",
    width: "100%",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  centeredContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorText: {
    marginBottom: 10,
    fontSize: 16,
    fontWeight: "bold",
  },
});

export default GameScreen;
